/** Page size for the media grid. The server paginates; the client just asks. */
export const IMAGES_PER_PAGE = 30

/** Ceiling the API enforces on `?perPage=`, so no client can ask for everything. */
export const MAX_PER_PAGE = 200

/**
 * A user's own optional throttle on images in flight, defaulted to the ceiling so
 * it never binds below the instance pool. Lower it only to deliberately go slower
 * than the box allows.
 */
export const DEFAULT_CONCURRENCY = 8

/** Users who may be generating at once. A further user queues for a slot. */
export const DEFAULT_MAX_CONCURRENT_USERS = 4

/**
 * The instance-wide pool of in-flight images, and the ONLY number that sets the
 * host's memory floor: each in-flight image is a separate `claude` subprocess
 * measured at ~392 MB RSS, so 4 ≈ 1.6 GB.
 *
 * Deliberately a shared pool rather than a per-user allowance. Argus is normally
 * driven by one person at a time, and a per-user cap of 1 meant a lone user crawled
 * through 600 images in ~2.5h while three quarters of the box sat idle. One user
 * now takes the whole pool; the round-robin scheduler splits it only when others
 * are actually generating.
 */
export const DEFAULT_MAX_CONCURRENT_IMAGES = 4

/** Per-image wall-clock budget for one `claude` subprocess. */
export const CLAUDE_TIMEOUT_MS = 60_000

export const CLAUDE_MAX_TURNS = 5

/* ── Session lifetime ─────────────────────────────────────────────────────────
   Argus is a one-shot working surface: a session's sites, images and alt text are
   deleted when it ends. Both limits therefore exist to bound how long abandoned
   client data can sit in the database, because in practice nobody clicks Log out.

   A running job counts as activity, so a long batch is never killed by the idle
   timer mid-run.
   ─────────────────────────────────────────────────────────────────────────── */

/** Hard ceiling on a session regardless of activity — covers a working day. */
export const SESSION_TTL_MS = 8 * 60 * 60 * 1000 // 8 hours

/**
 * Inactivity after which a session — and with it the connected site, its images and
 * their generated alt text — is swept.
 *
 * The browser heartbeats while its tab is VISIBLE, so this only counts down once
 * nobody is actually looking at the app. A running job also counts as activity, so a
 * long batch is never killed mid-run.
 */
export const SESSION_IDLE_TTL_MS = 2 * 60 * 60 * 1000 // 2 hours

/** How often the sweeper looks for expired and idle sessions. */
export const SESSION_SWEEP_INTERVAL_MS = 10 * 60 * 1000 // 10 minutes

/** Heartbeat cadence from a visible tab. Must be well under the idle window. */
export const SESSION_HEARTBEAT_MS = 2 * 60 * 1000 // 2 minutes

/**
 * Warn this far before the absolute expiry so a user mid-review can save rather
 * than discovering the loss afterwards.
 */
export const SESSION_WARN_BEFORE_MS = 5 * 60 * 1000 // 5 minutes

export const SESSION_COOKIE_NAME = 'argus_session'

/** SSE comment heartbeat — stops proxies from reaping an idle stream. */
export const SSE_HEARTBEAT_MS = 25_000

export const MIN_PASSWORD_LENGTH = 12

/** Max size of an uploaded SSH private key, in bytes. */
export const MAX_SSH_KEY_BYTES = 16 * 1024

export const CLAUDE_ALT_PROMPT = `Use the Read tool to view the image file at {{imagePath}}, then generate a concise, descriptive alt text for it.

Requirements:
- 1-2 sentences maximum
- Describe the key visual content objectively
- Be specific but concise (aim for under 125 characters)
- Do not start with "Image of" or "Photo of"
- Suitable for screen readers

Return ONLY the alt text string. No quotes, no explanation, no markdown.`
